import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import cookies from 'store/apis/cookies';
import { authSelector } from 'store/reducers/auth.reducer';

import type { CustomerSurveySubmitPayload } from './CustomerSurveyModal';
import {
  fetchCustomerSurveyEligibility,
  submitCustomerSurveyResponse,
  surveyFromApi,
} from './customerSurveyApi';
import type { CustomerSurveySource, CustomerSurveyTriggerType } from './customerSurveyApi';
import {
  clearChecked,
  hasBeenShown,
  hasChecked,
  markChecked,
  markShown,
  syncCustomerSurveySessionToken,
} from './customerSurveySession';
import { clearLoginDelay, remainingLoginDelayMs } from './surveyLoginDelay';
import type { SurveyConfig } from './types';

type UseCustomerSurveyPromptArgs = {
  source: CustomerSurveySource;
  triggerType?: CustomerSurveyTriggerType;
  enabled?: boolean;
  requestId?: string | number;
  recheckKey?: string | number;
  delayMs?: number;
  routeAllowed?: boolean;
};

type PendingSurvey = {
  config: SurveyConfig;
  source: CustomerSurveySource;
  requestId?: string | number;
};

function isVisibleElement(element: Element): boolean {
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;

  const rect = element.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) return true;

  return Array.from(element.children).some((child) => {
    const childRect = child.getBoundingClientRect();
    return childRect.width > 0 && childRect.height > 0;
  });
}

function hasBlockingDialog(): boolean {
  if (typeof document === 'undefined') return false;

  return Array.from(
    document.querySelectorAll('[role="dialog"], [aria-modal="true"], .modal, [data-modal-root]'),
  ).some(isVisibleElement);
}

export default function useCustomerSurveyPrompt({
  source,
  triggerType,
  enabled = true,
  requestId,
  recheckKey,
  delayMs = 0,
  routeAllowed = true,
}: UseCustomerSurveyPromptArgs) {
  const { profile, isFetching } = useSelector(authSelector);
  const token: string = cookies.load('authToken') ?? '';
  const isLoading = Boolean(isFetching);
  const hasIdentity = Boolean(profile) && Boolean(token);
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState<SurveyConfig | null>(null);
  const [promptSource, setPromptSource] = useState<CustomerSurveySource>(source);
  const [promptRequestId, setPromptRequestId] = useState<string | number | undefined>(undefined);
  const [pendingSurvey, setPendingSurvey] = useState<PendingSurvey | null>(null);
  const [delayElapsed, setDelayElapsed] = useState(delayMs <= 0);
  const [tabVisible, setTabVisible] = useState(true);
  const lastRecheckKey = useRef<string | number | undefined>(recheckKey);

  useEffect(() => {
    if (delayMs <= 0 || typeof document === 'undefined') return undefined;

    const sync = () => setTabVisible(!document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, [delayMs]);

  useEffect(() => {
    if (delayMs <= 0) {
      setDelayElapsed(true);
      return undefined;
    }

    if (!enabled || !hasIdentity || isLoading) {
      setDelayElapsed(false);
      return undefined;
    }

    const remaining = remainingLoginDelayMs(delayMs);
    if (remaining <= 0) {
      setDelayElapsed(true);
      return undefined;
    }

    const timer = setTimeout(() => setDelayElapsed(true), remaining);
    return () => clearTimeout(timer);
  }, [delayMs, enabled, hasIdentity, isLoading]);

  useEffect(() => {
    if (!enabled || !hasIdentity) {
      setOpen(false);
      setConfig(null);
      setPromptSource(source);
      setPromptRequestId(undefined);
      setPendingSurvey(null);
      return;
    }

    if (isLoading) return;
    syncCustomerSurveySessionToken(token);

    if (!delayElapsed || !routeAllowed || !tabVisible) return;

    if (hasBeenShown()) return;

    const activityChanged = lastRecheckKey.current !== recheckKey;
    lastRecheckKey.current = recheckKey;
    if (activityChanged) clearChecked(token, source);

    if (hasChecked(token, source)) return;

    let cancelled = false;

    if (delayMs > 0) clearLoginDelay();

    (async () => {
      try {
        const result = await fetchCustomerSurveyEligibility(token, {
          source,
          triggerType,
          requestId,
        });
        if (cancelled) return;

        markChecked(token, source);

        if (result.eligible === true && result.survey) {
          const resolvedSource = result.source ?? source;
          setPendingSurvey({
            config: surveyFromApi(result.survey),
            source: resolvedSource,
            requestId: resolvedSource === 'request' ? requestId : undefined,
          });
        } else {
          setOpen(false);
          setConfig(null);
          setPromptSource(source);
          setPromptRequestId(undefined);
          setPendingSurvey(null);
        }
      } catch {
        if (!cancelled) {
          markChecked(token, source);
          setOpen(false);
          setConfig(null);
          setPromptSource(source);
          setPromptRequestId(undefined);
          setPendingSurvey(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    isLoading,
    hasIdentity,
    token,
    source,
    triggerType,
    requestId,
    recheckKey,
    delayElapsed,
    routeAllowed,
    tabVisible,
    delayMs,
  ]);

  useEffect(() => {
    if (!pendingSurvey || hasBeenShown()) return undefined;

    let cancelled = false;

    const openWhenReady = () => {
      if (cancelled) return;

      if (hasBlockingDialog()) {
        window.setTimeout(openWhenReady, 500);
        return;
      }

      markShown();
      setConfig(pendingSurvey.config);
      setPromptSource(pendingSurvey.source);
      setPromptRequestId(pendingSurvey.requestId);
      setPendingSurvey(null);
      setOpen(true);
    };

    openWhenReady();

    return () => {
      cancelled = true;
    };
  }, [pendingSurvey]);

  const dismiss = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSubmit = useCallback(
    async (payload: CustomerSurveySubmitPayload) => {
      const result = await submitCustomerSurveyResponse({
        recommend: payload.recommend,
        satisfaction: payload.satisfaction,
        comment: payload.comment,
        source: promptSource,
        source_request_id: promptSource === 'request' ? promptRequestId : undefined,
      });
      return {
        thanksTitle: result.thanks_title,
        thanksBody: result.thanks_body,
      };
    },
    [promptSource, promptRequestId],
  );

  return { open, config, dismiss, handleSubmit };
}
